import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Clock, ArrowRight, Flame } from 'lucide-react';

const formatRemaining = (ms) => {
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
};

const TrialCountdown = ({ user, quota }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!user || user.plan !== 'trial' || !user.trial_ends_at) return null;

  const endTs = new Date(user.trial_ends_at).getTime();
  const remainingMs = endTs - now;
  const timeLabel = formatRemaining(remainingMs);
  const scansLeft = quota && typeof quota.remaining === 'number' ? quota.remaining : null;
  const trialLive = remainingMs > 0 && quota?.on_trial;
  const trialEnded = remainingMs <= 0;

  // Case 1: trial still live
  if (trialLive) {
    const urgent = (remainingMs < 4 * 3600_000) || scansLeft === 0;
    return (
      <div className={`rounded-2xl border p-4 flex items-center justify-between gap-4 ${urgent ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${urgent ? 'bg-orange-100 text-orange-600' : 'bg-emerald-100 text-emerald-700'}`}>
            {urgent ? <Flame className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold text-emerald-950 truncate">
              {scansLeft !== null && (<span>{scansLeft === 0 ? 'No scans left' : `${scansLeft} scan${scansLeft === 1 ? '' : 's'} left`} · </span>)}
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />trial ends in {timeLabel || 'less than a minute'}</span>
            </p>
            <p className="text-emerald-950/60 text-sm">
              {scansLeft === 0
                ? 'Upgrade now to keep scanning without waiting.'
                : urgent
                  ? 'Your trial is ending soon — pick a plan before you lose Pro features.'
                  : 'Try the recipe library and unlimited scans before your trial ends.'}
            </p>
          </div>
        </div>
        <Link to="/pricing" className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-1 ${urgent ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-emerald-700 hover:bg-emerald-800 text-white'}`}>
          Upgrade <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  // Case 2: trial ended (within the last 7 days), user hasn't upgraded → prompt
  if (trialEnded) {
    const hoursSinceEnd = (now - endTs) / 3600_000;
    if (hoursSinceEnd > 24 * 7) return null; // stop nagging after a week
    return (
      <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-800 text-white p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center shrink-0"><Sparkles className="w-5 h-5" /></div>
          <div className="min-w-0">
            <p className="font-display font-bold text-lg truncate">Your trial has ended</p>
            <p className="text-white/85 text-sm">You're now on the Free plan (3 scans/day, no recipes). Upgrade for unlimited scans, macros and the recipe library.</p>
          </div>
        </div>
        <Link to="/pricing" className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold bg-white text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1">
          See plans <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  return null;
};

export default TrialCountdown;
